import { isSafeUrl } from './htmlPolicy';

// Where a video plays from. A merchant pastes a whole address far more often than they paste an id,
// so the id is pulled out of whatever they pasted and the embed address is then built here, from that
// id alone. Nothing a merchant typed is ever used as the frame's `src`.

/** The three places a video can come from, the same vocabulary `themes/catalog/video.ts` already uses. */
export const VIDEO_PROVIDERS = ['youtube', 'vimeo', 'file'] as const;
export type VideoProvider = (typeof VIDEO_PROVIDERS)[number];

/**
 * How a video behaves before anyone touches it, and how much of the provider's own furniture it wears.
 * `showTitle` is Vimeo's title, byline and portrait, which is the only chrome a provider lets a page
 * turn off.
 */
export type VideoPlayback = { autoplay: boolean; loop: boolean; muted: boolean; controls: boolean; showTitle: boolean };

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{6,32}$/;
const VIMEO_ID_RE = /^[0-9]{6,12}$/;

/** Where a YouTube id hides in the four addresses YouTube hands out. */
const YOUTUBE_URL_RE = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,32})/;
const VIMEO_URL_RE = /vimeo\.com\/(?:video\/)?([0-9]{6,12})/;

// Pulls the id of a provider video out of an id or out of whichever address the merchant pasted.
export function readVideoId(raw: string, provider: VideoProvider): string | null {
  const value = raw.trim();
  if (value === '') return null;
  const direct = provider === 'vimeo' ? VIMEO_ID_RE : YOUTUBE_ID_RE;
  if (direct.test(value)) return value;
  const found = (provider === 'vimeo' ? VIMEO_URL_RE : YOUTUBE_URL_RE).exec(value);
  return found ? found[1] : null;
}

// Builds the embed address for a provider video, out of an id this file checked and nothing else.
export function videoEmbedUrl(provider: VideoProvider, id: string, playback: VideoPlayback): string | null {
  if (provider === 'file') return null;
  const query = new URLSearchParams({
    autoplay: playback.autoplay ? '1' : '0',
    muted: playback.muted ? '1' : '0',
    loop: playback.loop ? '1' : '0'
  });
  if (provider === 'vimeo') {
    if (!playback.showTitle) for (const off of ['title', 'byline', 'portrait']) query.set(off, '0');
    return `https://player.vimeo.com/video/${id}?${query.toString()}`;
  }
  query.set('mute', playback.muted ? '1' : '0');
  if (playback.loop) query.set('playlist', id);
  if (!playback.controls) query.set('controls', '0');
  return `https://www.youtube-nocookie.com/embed/${id}?${query.toString()}`;
}

// Reads a hosted file's address, or nothing when its scheme is one no player should be pointed at.
export function readVideoFile(raw: string | undefined): string | undefined {
  return raw && isSafeUrl(raw, false) ? raw : undefined;
}
